import { CountMetric, LatencyMetric, EnvironmentDetail, UnsupportedEnvironmentException, PromiseUtils } from '@amzn/ihm-associate-tools-js-lib'
import * as CandyCaneService from '@services/CandyCaneService'
import { BluetoothPairedDevicesService } from './BluetoothPairedDevicesService'
import { CandyCaneBluetoothErrorMappings } from './CandyCaneBluetoothErrorMappings'

const BluetoothConnectionTimeout = Object.freeze({
    DEFAULT: 10000,
})

const BluetoothDeviceType = Object.freeze({
    HEADSET: 'HEADSET',
    HUMAN_INTERFACE_DEVICE: 'HUMAN_INTERFACE_DEVICE',
    BLUETOOTH_SERIAL_COMMUNICATION_DEVICE: 'BLUETOOTH_SERIAL_COMMUNICATION_DEVICE',
})

const BluetoothDeviceTypeToProfile = Object.freeze({
    HEADSET: 'HSP',
    HUMAN_INTERFACE_DEVICE: 'HID',
    BLUETOOTH_SERIAL_COMMUNICATION_DEVICE: 'BSC',
})

const programName = 'IhmFlyingDutchman'

export class BluetoothCoreService {

    // Public functions

    /**
     * Attempts to pair a given Bluetooth device. Immediately resolves if device is already paired.
     * @return {Promise}    - success when the device is paired.
     *                      - exception when plugin returns error
     */

    public static pairDevice (args: any[]) {

        if (!EnvironmentDetail.isAndroid()) {
            console.error('Bluetooth is only supported on Android')
            return Promise.reject(new UnsupportedEnvironmentException({
                message: 'Bluetooth is only supported on Android.',
            }))
        }

        const pairDeviceWithOptionsFaultMetrics = new CountMetric(undefined, 'Fault', programName, 'bluetoothCoreService.pairDevice')
        const pairDeviceWithOptionsLatencyMetrics = new LatencyMetric(undefined, 'Time', programName, 'bluetoothCoreService.pairDevice')
        pairDeviceWithOptionsLatencyMetrics.start()
        console.info('ATBase BluetoothCoreService pairDevice: calling CandyCaneService.bluetoothPair() with ' + JSON.stringify(args))
        return CandyCaneService.bluetoothPair(args)
            .then(() => {
                pairDeviceWithOptionsFaultMetrics.count(0)
                const deviceType = args[2] // callers will need to send this.
                return BluetoothPairedDevicesService.setLastPairedDevice(deviceType, args[0]) // args[0] returns mac address
                .then(() => {
                    console.info('Last paired bluetooth device successfully updated with device: ' + args[0] + ' from service: bluetoothCoreService')
                })
                .catch((error) => {
                    console.error('Last paired bluetooth device not updated with error: ',JSON.stringify(error))
                    pairDeviceWithOptionsFaultMetrics.count(1)
                    return Promise.reject(error)
                })
            })
            .catch((error) => {
                console.error('Bluetooth pairing unsuccessful with error: ',JSON.stringify(error))
                pairDeviceWithOptionsFaultMetrics.count(1)
                return Promise.reject(CandyCaneBluetoothErrorMappings.getExceptionForError(error))
            })
            .finally(() => {
                pairDeviceWithOptionsLatencyMetrics.stop()
            })
    }

    /**
     * Attempts to pair a given Bluetooth device with options. Immediately resolves if device is already paired.
     * @param args Argument requires macAddress and filters (currently only profile filter is supported) for example,
     * [
     *   {
     *     macAddress: "1A:1A:1A:1A:1A:1A",
     *     filters: [
     *       {
     *         profile: "HSP"
     *       }
     *     ]
     *   }
     * ]
     *
     * @return {Promise}    - success when the device is paired.
     *                      - exception when plugin returns error
     */

    public static pairDeviceWithOptions (args: any[]) {
        if (!EnvironmentDetail.isAndroid()) {
            console.error('Bluetooth is only supported on Android')
            return Promise.reject(new UnsupportedEnvironmentException({
                message: 'Bluetooth is only supported on Android.',
            }))
        }

        const pairDeviceWithOptionsFaultMetrics = new CountMetric(undefined, 'Fault', programName, 'bluetoothCoreService.pairDeviceWithOptions')
        const pairDeviceWithOptionsLatencyMetrics = new LatencyMetric(undefined, 'Time', programName, 'bluetoothCoreService.pairDeviceWithOptions')
        pairDeviceWithOptionsLatencyMetrics.start()
        console.info('ATBase BluetoothCoreService pairDeviceWithOptions: calling CandyCaneService.bluetoothPairWithOptions() with ' + JSON.stringify(args))
        return CandyCaneService.bluetoothPairWithOptions(args)
            .then(() => {
                console.info('Bluetooth device pairing with options was successful')
                pairDeviceWithOptionsFaultMetrics.count(0)
                const deviceType = args[0].deviceType
                return BluetoothPairedDevicesService.setLastPairedDevice(deviceType, args[0].macAddress)
                .then(() => {
                    console.info('Last paired bluetooth device successfully updated with device: ' + args[0].macAddress + ' from service: bluetoothCoreService')
                })
                .catch((error) => {
                    console.error('Last paired bluetooth device not updated with error: ',JSON.stringify(error))
                    return Promise.reject(error)
                })
            })
        .catch((error) => {
            console.error('Bluetooth pairing unsuccessful with error: ',JSON.stringify(error))
            pairDeviceWithOptionsFaultMetrics.count(1)
            return Promise.reject(CandyCaneBluetoothErrorMappings.getExceptionForError(error))
        })
        .finally(() => {
            pairDeviceWithOptionsLatencyMetrics.stop()
        })
    }

    /**
     * Attempts to connect a given Bluetooth device.
     * @param args Argument requires macAddress and bluetooth profile for connection for example,
     * [
     *   {
     *     macAddress: "1A:1A:1A:1A:1A:1A",
     *     profile: "HSP"
     *   }
     * ]
     *
     * @return {Promise}    - success when the device is connected.
     *                      - exception when plugin returns error
     */
    public static connectDevice (args) {
        if (!EnvironmentDetail.isAndroid()) {
            console.error('Bluetooth is only supported on Android')
            return Promise.reject(new UnsupportedEnvironmentException({
                message: 'Bluetooth is only supported on Android.',
            }))
        }

        const connectDeviceFaultMetrics = new CountMetric(undefined, 'Fault', programName, 'bluetoothCoreService.connectDevice')
        const connectDeviceLatencyMetrics = new LatencyMetric(undefined, 'Time', programName, 'bluetoothCoreService.connectDevice')
        connectDeviceLatencyMetrics.start()
        console.info('ATBase BluetoothCoreService connectDevice: calling CandyCaneService.bluetoothConnect(): ' + JSON.stringify(args))
        const bluetoothConnectPromise = CandyCaneService.bluetoothConnect(args)
        .then(() => {
            console.info('Bluetooth device connection was successful')
            connectDeviceFaultMetrics.count(0)
        })
        return PromiseUtils.executeWithTimeout(bluetoothConnectPromise, {
            durationMillis: BluetoothConnectionTimeout.DEFAULT,
            error: new Error('bluetoothCoreService connectDevice() execution timed out.'),
        })
        .catch((error) => {
            console.error('Bluetooth pairing unsuccessful with error: ',error)
            connectDeviceFaultMetrics.count(1)
            return Promise.reject(CandyCaneBluetoothErrorMappings.getExceptionForError(error))
        }).finally(() => {
            connectDeviceLatencyMetrics.stop()
        })
    }

    /**
     * Attempts to get information of the Bluetooth device.
     * @param args Argument requires macAddress for example,
     * [
     *   {
     *     macAddress: "1A:1A:1A:1A:1A:1A"
     *   }
     * ]
     *
     * @return {Promise}    - success when the device information is successfully retrieved.
     *                      - exception when plugin returns error
     */
    public static getDeviceInfo (args: any[]) {
        if (!EnvironmentDetail.isAndroid()) {
            console.error('Bluetooth is only supported on Android')
            return Promise.reject(new UnsupportedEnvironmentException({
                message: 'Bluetooth is only supported on Android.',
            }))
        }

        const getDeviceInfoFaultMetrics = new CountMetric(undefined, 'Fault', programName, 'bluetoothCoreService.getDeviceInfo')
        const getDeviceInfoLatencyMetrics = new LatencyMetric(undefined, 'Time', programName, 'bluetoothCoreService.getDeviceInfo')
        getDeviceInfoLatencyMetrics.start()
        console.info('ATBase BluetoothCoreService getDeviceInfo: calling CandyCaneService.bluetoothGetDeviceInfo(): ' + JSON.stringify(args))
        return CandyCaneService.bluetoothGetDeviceInfo(args)
            .then((deviceInfo) => {
                console.info('Bluetooth device information was successfully retrieved')
                getDeviceInfoFaultMetrics.count(0)
                return deviceInfo
            })
            .catch((error) => {
                console.error('Unable to get device info with error: ', error)
                getDeviceInfoFaultMetrics.count(1)
                return Promise.reject(CandyCaneBluetoothErrorMappings.getExceptionForError(error))
            })
            .finally(() => {
                getDeviceInfoLatencyMetrics.stop()
            })
    }

    /**
     * Sends print job to a connected bluetooth printer.
     * @return {Promise}     - success when the printer has received the job.
     *                       - exception when plugin returns error
     */
    public static bluetoothPrint (args) {
        if (!EnvironmentDetail.isAndroid()) {
            console.error('Bluetooth is only supported on Android')
            return Promise.reject(new UnsupportedEnvironmentException({
                message: 'Bluetooth print is only supported on Android.',
            }))
        }

        const bluetoothPrintFaultMetrics = new CountMetric(undefined, 'Fault', programName, 'bluetoothCoreService.bluetoothPrint')
        const bluetoothPrintLatencyMetrics = new LatencyMetric(undefined, 'Time', programName, 'bluetoothCoreService.bluetoothPrint')
        bluetoothPrintLatencyMetrics.start()
        console.info('ATBase BluetoothCoreService bluetoothPrint: calling CandyCaneService.bluetoothPrint(): ' + JSON.stringify(args))
        return CandyCaneService.bluetoothPrint(args)
            .then(() => {
                console.info('Bluetooth print job succesfully sent')
                bluetoothPrintFaultMetrics.count(0)
            })
            .catch((error) => {
                console.error('bluetoothCoreService bluetoothPrint() error ', error)
                bluetoothPrintFaultMetrics.count(1)
                return Promise.reject(CandyCaneBluetoothErrorMappings.getExceptionForError(error))
            })
            .finally(() => {
                bluetoothPrintLatencyMetrics.stop()
            })
    }
}
